import {
  Box,
  Button,
  Divider,
  Step,
  Stepper,
  Tab,
  Tabs,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import StepLabel from '@mui/material/StepLabel'
import { styled } from '@mui/material/styles'
import axios from 'api/axios'
import { CustomModalContext } from 'common/contexts/CustomModalContext'
import { ModalTypes } from 'common/hooks/useCustomModal'
import { enqueueSnackbar } from 'notistack'
import { useContext, useEffect, useRef, useState } from 'react'
import EvaluacionQuestion from './EvaluacionQuestion'

const CustomStepLabel = styled(StepLabel)(({ theme }) => ({
  '& .MuiStepIcon-root': {
    color: theme.palette.grey[300],
    '&.Mui-active': {
      color: theme.palette.success.main,
    },
    '&.Mui-completed': {
      color: theme.palette.success.main,
    },
  },
  '& .MuiStepLabel-labelContainer': {
    '@media (min-width: 960px)': {
      textAlign: 'center',
    },
    '@media (max-width: 960px)': {
      display: 'none',
    },
  },
}))

const EvaluacionStepper = ({
  evaluacion,
  sx,
  onBack,
  evaluado,
  evaluadorDni,
  onAnswerSaved = () => {},
}) => {
  const { handleModal } = useContext(CustomModalContext)
  const theme = useTheme()
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'))

  const [activeStep, setActiveStep] = useState(0)
  const [answers, setAnswers] = useState(() =>
    (evaluacion ?? []).flatMap((cap) =>
      (cap.preguntas ?? []).map((p) => ({
        codigo_capitulo: cap.codigo_capitulo,
        codigo_aspecto: p.codigo_aspecto,
        valor: p.valor ?? null,
        porcentaje: p.porcentaje ?? null,
        observaciones: p.observaciones ?? null,
      })),
    ),
  )
  const answersRef = useRef([])
  const patchTimersRef = useRef({})

  useEffect(() => {
    const mainDiv = document.querySelector('.main')
    mainDiv.scrollTo({ top: 0, behavior: 'smooth' })
  }, [activeStep])

  const scrollToQuestion = (codigoAspecto) => {
    const element = document.getElementById(`question-${codigoAspecto}`)
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }
  }

  const allQuestionsAnswered = () => {
    for (let i = 0; i < evaluacion.length; i++) {
      const capitulo = evaluacion[i]
      for (let j = 0; j < capitulo.preguntas.length; j++) {
        const pregunta = capitulo.preguntas[j]
        const respuesta = answers.filter(
          (answer) =>
            answer.codigo_aspecto === pregunta.codigo_aspecto &&
            answer.codigo_capitulo === capitulo.codigo_capitulo,
        )[0]
        if (pregunta.tipo === 'T') {
          if (!respuesta.observaciones || respuesta.observaciones.trim() === '') {
            scrollToQuestion(pregunta.codigo_aspecto)
            enqueueSnackbar(`Pregunta sin respuesta: ${pregunta.titulo_aspecto}`, {
              variant: 'warning',
            })
            return false
          }
        } else {
          if (!respuesta.valor && !respuesta.porcentaje) {
            scrollToQuestion(pregunta.codigo_aspecto)
            enqueueSnackbar(`Pregunta sin respuesta: ${pregunta.titulo_aspecto}`, {
              variant: 'warning',
            })
            return false
          }
        }
      }
      return true
    }
  }

  const handleSave = async (_id) => {
    try {
      const body = { respuestas: answers }
      if (evaluadorDni) body.evaluador_dni = evaluadorDni
      await axios.put(`/desempeno_detalle_list/${evaluado.id}/`, body)
      enqueueSnackbar('Respuestas guardadas con éxito.', { variant: 'success' })
      return true
    } catch (error) {
      enqueueSnackbar(`Error al guardar las respuestas.`, { variant: 'error' })
      return false
    }
  }

  const handleChange = (respuesta) => {
    const { codigo_capitulo, codigo_aspecto, valor, porcentaje, observaciones } = respuesta
    setAnswers((prevAnswers) => {
      const updatedAnswers = prevAnswers.filter(
        (answer) =>
          !(answer.codigo_aspecto === codigo_aspecto && answer.codigo_capitulo === codigo_capitulo),
      )
      updatedAnswers.push({
        codigo_capitulo,
        codigo_aspecto,
        valor,
        porcentaje,
        observaciones,
      })
      answersRef.current = updatedAnswers
      return updatedAnswers
    })
  }

  const handlePatchAnswer = (respuesta) => {
    const key = `${respuesta.codigo_capitulo}-${respuesta.codigo_aspecto}`
    if (patchTimersRef.current[key]) clearTimeout(patchTimersRef.current[key])
    patchTimersRef.current[key] = setTimeout(async () => {
      try {
        const body = { respuestas: [respuesta] }
        if (evaluadorDni) body.evaluador_dni = evaluadorDni
        await axios.patch(`/desempeno_detalle_list/${evaluado.id}/`, body)
        onAnswerSaved(answersRef.current)
      } catch {
        // auto-save silencioso — el botón GUARDAR sigue disponible como fallback
      }
    }, 500)
  }

  const handleFinish = async () => {
    handleModal({
      type: ModalTypes.Loading,
      enabled: true,
      content: <Typography>Finalizando Evaluación</Typography>,
    })

    if (allQuestionsAnswered()) {
      try {
        const body = { respuestas: answers }
        if (evaluadorDni) body.evaluador_dni = evaluadorDni
        await axios.post(`/desempeno_detalle_list/${evaluado.id}/`, body)
        enqueueSnackbar('Evaluación Finalizada con Éxito', {
          variant: 'success',
        })
        handleModal({ type: ModalTypes.Loading, enabled: false })
        onBack()
      } catch (_error) {
        enqueueSnackbar(`Error al finalizar la evaluación.`, { variant: 'error' })
        handleModal({ type: ModalTypes.Loading, enabled: false })
      }
    } else {
      handleModal({ type: ModalTypes.Loading, enabled: false })
    }
  }

  const isLast = activeStep === evaluacion.length - 1

  if (isDesktop) {
    return (
      <Box sx={{ display: 'flex', ...sx }}>
        {/* Panel izquierdo: Tabs verticales */}
        <Tabs
          orientation="vertical"
          value={activeStep}
          onChange={(_, v) => setActiveStep(v)}
          sx={{
            borderRight: 1,
            borderColor: 'divider',
            minWidth: 180,
            '& .MuiTab-root': { alignItems: 'flex-start', textAlign: 'left' },
          }}
        >
          {evaluacion.map((cap) => (
            <Tab key={cap.codigo_capitulo} label={cap.titulo_capitulo} />
          ))}
        </Tabs>

        {/* Panel derecho: contenido del capítulo activo */}
        <Box sx={{ flex: 1, p: 3 }}>
          {evaluacion[activeStep]?.detalle_capitulo && (
            <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary' }}>
              {evaluacion[activeStep].detalle_capitulo}
            </Typography>
          )}
          {evaluacion[activeStep]?.preguntas.map((pregunta, i) => (
            <EvaluacionQuestion
              key={`question-${i}`}
              question={pregunta}
              codigo_capitulo={evaluacion[activeStep]?.codigo_capitulo}
              onChange={handleChange}
              onAutoSave={handlePatchAnswer}
            />
          ))}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
            <Button disabled={activeStep === 0} onClick={() => setActiveStep((s) => s - 1)}>
              Atrás
            </Button>
            <Button onClick={handleSave}>Guardar</Button>
            {isLast ? (
              <Button onClick={handleFinish}>Finalizar</Button>
            ) : (
              <Button onClick={() => setActiveStep((s) => s + 1)}>Siguiente</Button>
            )}
          </Box>
        </Box>
      </Box>
    )
  }

  return (
    <Box sx={{ ...sx }}>
      {/* Stepper para mostrar los capítulos */}
      <Stepper activeStep={activeStep} orientation="horizontal" alternativeLabel>
        {evaluacion.map((capitulo) => (
          <Step key={`step-${capitulo.codigo_capitulo}`}>
            <CustomStepLabel>{capitulo.titulo_capitulo}</CustomStepLabel>
          </Step>
        ))}
      </Stepper>

      {/* Contenido del capítulo actual */}
      {evaluacion.map((capitulo, index) => (
        <Box sx={{ mt: 2, display: activeStep === index ? 'block' : 'none' }} key={index}>
          {capitulo.detalle_capitulo ? (
            <Box>
              <Divider sx={{ my: 4 }} />
              <Typography>{capitulo.detalle_capitulo}</Typography>
              <Divider sx={{ my: 4 }} />
            </Box>
          ) : (
            <></>
          )}
          {capitulo?.preguntas.map((pregunta, i) => (
            <EvaluacionQuestion
              key={`question-${i}`}
              question={pregunta}
              codigo_capitulo={capitulo?.codigo_capitulo}
              onChange={handleChange}
              onAutoSave={handlePatchAnswer}
            />
          ))}
        </Box>
      ))}

      {/* Botones de navegación */}
      <Box sx={{ mt: 2 }}>
        <Box sx={{ display: 'flex', flexDirection: 'row', pt: 2 }}>
          <Button
            color="inherit"
            disabled={activeStep === 0}
            onClick={() => setActiveStep(activeStep - 1)}
            sx={{ mr: 1 }}
          >
            Atrás
          </Button>
          <Box sx={{ flex: '1 1 auto' }} />
          <Button onClick={handleSave} disabled={false} sx={{ mr: 1 }}>
            Guardar
          </Button>

          {activeStep === evaluacion.length - 1 ? (
            <Button onClick={handleFinish}>Finalizar</Button>
          ) : (
            <Button onClick={() => setActiveStep(activeStep + 1)}>Siguiente</Button>
          )}
        </Box>
      </Box>
    </Box>
  )
}

export default EvaluacionStepper
